import { maybe, rd, RemoteData } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { ThunkDispatch, unwrapResult } from '@reduxjs/toolkit';
import { IntegrationID, type Group, type Resource } from '__types__';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction } from 'redux';
import { selectIntegrationById } from 'store/integrations';
import { getNetSuiteMasterProjectsRequest } from 'store/netsuite';
import type { RootStore } from 'store/reducer';
import {
  resourceQueryUtils,
  ResourceSearchQuery
} from 'v5/api/resource/resource.api';
import {
  renderResourceGroupOption,
  createResourceGroupValueRenderer
} from 'v5/features/resources/resourceGroupOptions';
import {
  agentDetailsOf,
  isAgentRef,
  agentPickUpdates
} from 'v5/core/entity-values/codecs/entity-ref.agent';
import type { ResourceFormKitConfig } from 'v5/patterns/forms/schema-form/fields/resourceFormKit';
import type {
  NetsuiteMasterProject,
  NetsuiteMasterProjectsState
} from 'v5/patterns/forms/schema-form/fields/NetsuiteMasterProjectField';
import type { SchemaFormHandle } from 'v5/patterns/forms/schema-form/schemaForm.types';
import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { WithServices } from 'v5/platform/ts/services';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { usesSingleNameField } from 'v5/core/entity-schema/resource/fieldSpecs';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { Permission } from 'v5/services/front/PermissionService/PermissionService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { toCountryCode } from 'v5/patterns/widgets/PhoneInput/country';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithGeolocationService } from 'v5/services/io/GeolocationService/GeolocationService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import type { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { resourceDisplayName } from 'v5/patterns/resource/resourceDisplayName';
import {
  EntityChip,
  EntityOption,
  type EntityOptionData
} from 'v5/patterns/widgets/EntityOption';
import type {
  EntityCardData,
  EntityCardFact
} from 'v5/patterns/widgets/EntityHoverCard';
import { getPrimaryEmail } from 'v5/core/contact-details/primaryEmail';
import { getContactDetailPath } from 'v5/core/app-scope/entityWorkspaceRouting';
import { RESOURCE_CORE_FIELD_ICONS } from 'v5/patterns/resource/resourceFieldIcons';

export type ResourceFormKitServices = WithServices<
  [
    WithGeolocationService,
    WithResourceService,
    /** Opening a referenced resource. */
    WithUserFlowService,
    /** A failed read of a picked agent or a failed inline create. */
    WithToastService,
    /** Creating a referenced resource from its typed name; its page link. */
    WithAppScopeService,
    WithEnterpriseService,
    WithAccessService,
    WithResourceTypeService,
    WithResourceMutationService,
    /** The browsing workspace's country, for new phone numbers. */
    WithSystemSettingsService
  ]
>;

/** The v1 thunk store is untyped; this is the dispatch the NetSuite thunk needs. */
type ThunkStoreDispatch = ThunkDispatch<RootStore, undefined, AnyAction>;

/** `GET /integrations/splashlight/netsuite_master_projects` rows, parsed defensively. */
function parseMasterProjects(data: unknown): NetsuiteMasterProject[] {
  if (!Array.isArray(data)) return [];
  const projects: NetsuiteMasterProject[] = [];
  for (const row of data) {
    if (typeof row !== 'object' || row === null) continue;
    const record: Record<string, unknown> = row;
    const id = Number(record.id);
    if (!Number.isInteger(id)) continue;
    projects.push({
      id,
      name: typeof record.name === 'string' ? record.name : String(id)
    });
  }
  return projects.sort((a, b) => a.name.localeCompare(b.name));
}

function toError(cause: unknown): Error {
  if (cause instanceof Error) return cause;
  const message =
    typeof cause === 'object' &&
    cause !== null &&
    'message' in cause &&
    typeof cause.message === 'string'
      ? cause.message
      : 'Failed to load NetSuite master projects';
  return new Error(message);
}

/**
 * NetSuite master projects the same way the legacy
 * `SplashlightNetsuiteMasterProjectSelector` read them: the integration flag
 * from the v1 store, the list through the `store/netsuite` thunk. Redux stays
 * here in the composition layer; the engine only sees `RemoteData`.
 */
function useNetsuiteMasterProjects(): RemoteData<NetsuiteMasterProjectsState> {
  const dispatch = useDispatch<ThunkStoreDispatch>();
  const integration = useSelector((state: RootStore) =>
    selectIntegrationById(state, IntegrationID.NETSUITE)
  );
  const enabled = Boolean(integration?.enabled);
  const [projects, setProjects] = useState<RemoteData<NetsuiteMasterProject[]>>(
    rd.ofPending()
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setProjects(rd.ofPending());
    dispatch(getNetSuiteMasterProjectsRequest())
      .then(unwrapResult)
      .then(response => {
        if (cancelled) return;
        setProjects(rd.of(parseMasterProjects(response?.data)));
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProjects(rd.ofError(toError(cause)));
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, enabled]);

  return useMemo(
    () =>
      enabled
        ? rd.map(projects, list => ({
            type: 'enabled' as const,
            projects: list
          }))
        : rd.of<NetsuiteMasterProjectsState>({ type: 'disabled' }),
    [enabled, projects]
  );
}

/**
 * What the resource kit needs from the app: the service-bound fields
 * (location search, agent / organisation lookup and links, an agent's
 * details copied on pick, group badges).
 *
 * The kit's components are built from it, so it must keep its identity
 * across renders: a new config means a new component type for every
 * service-bound field, and React then unmounts and remounts those fields
 * (losing focus, ghost rows and typed text). It is therefore memoised on
 * `services` only; the group lists are read through a ref at render time.
 *
 * @param createForm the create form's handle: an agent picked there fills
 *   the resource's details at once (edit mode copies them on save instead).
 */
export function useResourceFormKitConfig(
  services: ResourceFormKitServices['services'],
  groups: Group[],
  selectedGroups: Group[],
  /** The resource edited (`null` while creating): its agent and organisation come with it. */
  resource: Resource | null,
  createForm?: RefObject<SchemaFormHandle<ResourceKinds> | null>
): ResourceFormKitConfig {
  const latest = useRef({ groups, selectedGroups, resource });
  latest.current = { groups, selectedGroups, resource };
  return useMemo(
    () =>
      ({
        contactDetails: {
          // As legacy `PHONE_NUMBERS`: new phones read in the browsing workspace's country.
          useDefaultCountry: () =>
            toCountryCode(
              rd.tryGet(
                services.systemSettingsService.useSetting(
                  services.appScopeService.useResolvedScopeIdentity(),
                  'defaults_country_code'
                )
              )?.value
            ) ?? null
        },
        locations: {
          useLocations: query => services.geolocationService.useLocations(query)
        },
        entityRef: {
          resource: {
            noun: 'resource',
            nameOf: id => embeddedResourceName(latest.current.resource, id),
            useOption: id =>
              ensureIdleQuery(
                id,
                rd.map(
                  services.resourceService.useItem(maybe.getOrUndefined(id)),
                  item => <EntityChip entity={resourceAsEntity(item)} />
                )
              ),
            useCard: id =>
              ensureIdleQuery(
                id,
                rd.map(
                  services.resourceService.useItem(maybe.getOrUndefined(id)),
                  resourceCard
                )
              ),
            useOptions: (query, { resourceTypeModels: models }) => {
              const filters: Partial<ResourceSearchQuery['filters']> = {
                resource_type_model: { operator: 'matchAny', value: models }
              };
              return rd.map(
                services.resourceService.useList(
                  resourceQueryUtils
                    .getBuilder()
                    .build(b => [
                      b.withSearch(query),
                      b.withMergedFilters(filters)
                    ])
                ),
                list =>
                  rowLevelValidation
                    .validDataFromRows(list.items)
                    .map(item => ({
                      value: item.id,
                      label: <EntityOption entity={resourceAsEntity(item)} />
                    }))
              );
            },
            // Like legacy `ResourceSelector`: straight from the typed name, into
            // the first type of the model, visible where the user browses.
            useCreate: ({ resourceTypeModels: models }) => {
              const scopeRd = services.appScopeService.useResolvedAppScope();
              const identity = rd.tryMap(
                scopeRd,
                appScopeUtils.identityFromResolved
              );
              const canCreate = rd.tryGet(
                services.accessService.useCan(identity)({
                  action: Permission.RESOURCE_CREATE
                })
              );
              const type = rd
                .tryGet(services.resourceTypeService.useResourceTypes(identity))
                ?.find(type => type.model && models.includes(type.model));
              const scope = rd.tryGet(scopeRd);
              if (canCreate !== true || !type?.model || !scope) return null;
              const model = type.model;
              return async name => {
                const [first = '', ...rest] = name.split(/\s+/);
                const changes = usesSingleNameField(model)
                  ? [{ column: 'first_name' as const, value: name }]
                  : [
                      { column: 'first_name' as const, value: first },
                      { column: 'last_name' as const, value: rest.join(' ') }
                    ];
                try {
                  const created = await services.resourceMutationService.create(
                    {
                      resourceTypeId: type.id,
                      profilePicture: null,
                      membership:
                        scope.level === 'organization'
                          ? { type: 'global' }
                          : {
                              type: 'workspaces',
                              workspaceIds: [scope.workspaceId]
                            },
                      patches: [{ kind: 'core', changes }]
                    }
                  );
                  return created.id;
                } catch (cause) {
                  services.toastService.open({
                    type: 'error',
                    message: ensureError(cause).message
                  });
                  return null;
                }
              };
            },
            open: id => services.userFlowService.openResourceSidebar(id),
            // As the resource lists: the page in the browsing scope.
            usePageHref: id => {
              const organizationId =
                services.appScopeService.useOrganizationId();
              const scope = rd.tryGet(
                services.appScopeService.useResolvedAppScope()
              );
              const workspaces = rd.tryGet(
                services.enterpriseService.useWorkspaces(organizationId)
              );
              return getContactDetailPath(
                organizationId,
                {
                  enterprise_workspace_id:
                    scope?.level === 'workspace' ? scope.workspaceId : null
                },
                workspaces,
                id
              );
            },
            // Edit mode copies on save instead (`resourceBindings`).
            onPick: ({ spec, id }) => {
              if (!isAgentRef(spec) || createForm === undefined) return;
              services.resourceService
                .ensureItem(id)
                .then(agent => {
                  createForm.current?.updateFields(
                    agentPickUpdates(agentDetailsOf(agent))
                  );
                })
                .catch((cause: unknown) =>
                  services.toastService.open({
                    type: 'error',
                    message: ensureError(cause).message
                  })
                );
            }
          }
        },
        groups: {
          renderSelectedValue: props =>
            createResourceGroupValueRenderer(
              latest.current.groups,
              latest.current.selectedGroups
            )(props),
          renderOption: renderResourceGroupOption
        },
        netsuite: { useMasterProjects: useNetsuiteMasterProjects }
      }) satisfies ResourceFormKitConfig,
    [services, createForm]
  );
}

/** The name of the agent or organisation `id` that the resource's own payload carries; `null` for another one. */
export function embeddedResourceName(
  resource: Pick<Resource, 'organisation' | 'represented_by'> | null,
  id: number
): string | null {
  const named = [resource?.organisation, resource?.represented_by].find(
    ref => ref?.id === id
  );
  return named ? resourceDisplayName(named) : null;
}

/** A referenced resource in a picker: its picture, name, and its type and email under it. */
function resourceAsEntity(resource: {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  profile_picture?: string | null;
  resource_type?: { name?: string | null } | null;
  emails?: ReadonlyArray<{ id: number; value_1?: string | null }> | null;
}): EntityOptionData {
  const detail = [
    resource.resource_type?.name,
    getPrimaryEmail(resource.emails ?? [])
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    name: resourceDisplayName(resource),
    detail: detail === '' ? null : detail,
    pictureUrl: resource.profile_picture ?? null
  };
}

type DetailRow = {
  id: number;
  value_1?: string | null;
};

/** A referenced resource on its hover card: what it is, how to reach it, what it is marked with. */
function resourceCard(resource: {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  pronouns?: string | null;
  profile_picture?: string | null;
  resource_type?: { name?: string | null } | null;
  organisation?: {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    profile_picture?: string | null;
  } | null;
  emails?: ReadonlyArray<DetailRow> | null;
  phone_numbers?: ReadonlyArray<DetailRow> | null;
  websites?: ReadonlyArray<DetailRow> | null;
  locations?: ReadonlyArray<{ name?: string | null }> | null;
  groups?: ReadonlyArray<{
    name?: string | null;
    colour?: string | null;
  }> | null;
  tags?: ReadonlyArray<{ tag: string }> | null;
}): EntityCardData {
  const filled = (rows: ReadonlyArray<DetailRow> | null | undefined) =>
    (rows ?? []).filter(row => (row.value_1 ?? '').trim() !== '');
  const primary = getPrimaryEmail(resource.emails ?? []);
  // The primary email first, the others as saved.
  const emails = [...filled(resource.emails)].sort(
    (a, b) =>
      Number(b.value_1?.trim() === primary) -
      Number(a.value_1?.trim() === primary)
  );
  /** The first of a kind, and how many more there are. */
  const firstOf = (
    rows: ReadonlyArray<Omit<EntityCardFact, 'more'>>
  ): EntityCardFact[] =>
    rows.length === 0 ? [] : [{ ...rows[0], more: rows.length - 1 }];
  const facts: EntityCardFact[] = [
    ...firstOf(
      emails.map(row => ({
        icon: <RESOURCE_CORE_FIELD_ICONS.emails aria-hidden />,
        text: row.value_1?.trim() ?? '',
        href: `mailto:${row.value_1?.trim() ?? ''}`
      }))
    ),
    ...firstOf(
      filled(resource.phone_numbers).map(row => ({
        icon: <RESOURCE_CORE_FIELD_ICONS.phone_numbers aria-hidden />,
        text: row.value_1?.trim() ?? '',
        href: `tel:${(row.value_1 ?? '').replace(/[^\d+]/g, '')}`
      }))
    ),
    ...firstOf(
      filled(resource.websites).map(row => {
        const site = row.value_1?.trim() ?? '';
        return {
          icon: <RESOURCE_CORE_FIELD_ICONS.websites aria-hidden />,
          text: site,
          href: /^https?:\/\//.test(site) ? site : `https://${site}`
        };
      })
    ),
    ...firstOf(
      (resource.locations ?? [])
        .map(location => location.name?.trim() ?? '')
        .filter(name => name !== '')
        .map(name => ({
          icon: <RESOURCE_CORE_FIELD_ICONS.locations aria-hidden />,
          text: name,
          href: null
        }))
    )
  ];
  const chips = [
    ...(resource.groups ?? [])
      .filter(group => (group.name ?? '').trim() !== '')
      .map(group => ({
        label: group.name ?? '',
        colour: group.colour ?? null
      })),
    ...(resource.tags ?? []).map(tag => ({ label: tag.tag, colour: null }))
  ];
  const organisation = resource.organisation;
  return {
    name: resourceDisplayName(resource),
    pictureUrl: resource.profile_picture ?? null,
    kind: resource.resource_type?.name?.trim() || null,
    note: resource.pronouns?.trim() || null,
    partOf: organisation
      ? {
          name: resourceDisplayName(organisation),
          pictureUrl: organisation.profile_picture ?? null
        }
      : null,
    facts,
    chips
  };
}
